import {
  type ChangeEvent,
  type ComponentProps,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { AtomsPortalProvider, cn } from '@opendoor/atoms/utils';
import * as Sentry from '@sentry/nextjs';
import {
  IconBuilding,
  IconClock,
  IconHome,
  IconLocation,
  IconMapPin,
  IconX,
} from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import {
  MarketplaceLocationAddressesDocument,
  MarketplaceLocationSuggestionsDocument,
  MarketplaceLocationZonesDocument,
  type MarketplacePropertySuggestionFragment,
  type MarketplaceZoneSuggestionFragment,
  type MarketplaceZoneType,
} from 'components/marketplace/map/__generated__/location-search.zeus';
import type { SavedSearchFieldsFragment } from 'components/marketplace/savedSearches/__generated__/saved-searches.zeus';
import {
  SavedSearchesSection,
  type SavedSearchesSectionProps,
} from 'components/marketplace/savedSearches/SavedSearchesSection';
import { formatCurrency } from 'helpers/formatters';
import { US_STATE_CODES } from 'helpers/markets';
import { createPortal } from 'react-dom';
import { useDebounce } from 'use-debounce';

import {
  addressFromRecentEntry,
  mapLocationFromRecentEntry,
  recentEntryDisplayName,
  recentEntryFromAddress,
  recentEntryFromLocation,
  recentEntryIdentity,
  type RecentLocationSearchEntry,
} from './recentLocationSearches';
import type { MapLocation } from './types';
import { useRecentLocationSearches } from './useRecentLocationSearches';
import { getZoneFrameBounds } from './utils';

const zoneTypeLabel = {
  CITY: '(City)',
  COUNTY: '(County)',
  MACRO_NEIGHBORHOOD: '(Neighborhood)',
  NEIGHBORHOOD: '(Neighborhood)',
  RESIDENTIAL_BOUNDARY: '',
  STATE: '(State)',
  SUBNEIGHBORHOOD: '(Neighborhood)',
  UNKNOWN: '',
  ZIP_CODE: '(Zip code)',
} satisfies Record<MarketplaceZoneType, string>;

export type LocationSearchSelection =
  | { kind: 'location'; location: MapLocation }
  | { kind: 'address'; address: MarketplacePropertySuggestionFragment };

export interface LocationSearchProps {
  isFocused: boolean;
  selection: LocationSearchSelection | null;
  onSelectionChange: (selection: LocationSearchSelection | null) => void;
  /** Input height. `sm` (56px) on the desktop quick-filters bar; `xs` (36px) in
   * the compact mobile header. */
  size?: ComponentProps<typeof Input>['size'];
  /** Hides the visible label when the surrounding control bar supplies context. */
  showLabel?: boolean;
  inputClassName?: string;
  presentation?: 'default' | 'map-refresh';
  onNearby?: () => void;
  onInputEdit?: () => void;
  /** Text the input shows in ink when nothing is selected, such as "Homes in map area". Typing or clearing replaces it. */
  label?: string | null;
  onLabelClear?: () => void;
  nearbyPending?: boolean;
  nearbyError?: string | null;
  placeholder?: string;
  /** Optional action rendered beside Clear inside the input's trailing control group. */
  trailingControl?: ReactNode;
  /** Saved searches listed in the `map-refresh` panel while the query is empty. */
  savedSearches?: SavedSearchesSectionProps;
}

/** The dropdown's padding; the scrollbar's gutter completes it on the right. */
const RESULTS_PAD = 12;
const SCROLLBAR_W = 6;
/*
 * A thin scrollbar whose gutter is always reserved, so the rows sit in the
 * same place whether or not the list scrolls, and no extra padding appears
 * beside it. Firefox has no custom width; its thin scrollbar is close.
 */
const RESULTS_SCROLLBAR = `
[data-location-results][data-location-results]{ scrollbar-gutter:stable; scrollbar-width:thin; scrollbar-color:#D7D4CD transparent; padding-right:${RESULTS_PAD - SCROLLBAR_W}px; }
[data-location-results]::-webkit-scrollbar{ width:${SCROLLBAR_W}px; }
[data-location-results]::-webkit-scrollbar-track{ background:transparent; }
[data-location-results]::-webkit-scrollbar-thumb{ background:#D7D4CD; border-radius:${SCROLLBAR_W / 2}px; }
[data-location-results]::-webkit-scrollbar-thumb:hover{ background:#BDBAB3; }
`;

const QUERY_STRING_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;
const MAP_SEARCH_DEBOUNCE_MS = 150;
const MAP_SUGGESTION_CACHE_MS = 5 * 60_000;

// A state code is the one useful query at the minimum length.
const isSearchableQuery = (query: string) =>
  query.length > QUERY_STRING_MIN_LENGTH || US_STATE_CODES.includes(query.toUpperCase());

type RankedMapLocation = MapLocation & { population?: number };

const toRankedMapLocation = ({ zone }: MarketplaceZoneSuggestionFragment): RankedMapLocation => {
  const isState = zone.type === 'STATE';
  const bounds = isState ? getZoneFrameBounds(zone) : null;
  return {
    coordinates: zone.center
      ? ([zone.center.longitude, zone.center.latitude] satisfies [number, number])
      : undefined,
    label: isState ? zone.name : [zone.name, zone.stateAbbr].filter(Boolean).join(', '),
    population: zone.population ?? undefined,
    value: zone.id,
    zoneType: zone.type,
    ...(bounds ? { bounds } : {}),
  };
};

const reportLocationSuggestionsError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: { feature: 'marketplace-browse', operation: 'locationSuggestions' },
  });
};

/** React events also bubble out of portals; this limits a handler to its own DOM subtree. */
const targetsOwnSubtree = (event: SyntheticEvent<HTMLElement>) =>
  event.target instanceof Node && event.currentTarget.contains(event.target);

// Picking a row keeps focus in the search input. Text fields in the panel, and
// dialogs opened from it, still take focus.
const keepInputFocusOnRowPress = (event: ReactMouseEvent<HTMLElement>) => {
  if (!targetsOwnSubtree(event) || event.target instanceof HTMLInputElement) return;
  event.preventDefault();
};

const fetchLocationSuggestions = async (
  query: string,
  signal: AbortSignal,
  /** The map's search bar shows only places, so it skips the address search. */
  zonesOnly: boolean,
) => {
  const types: MarketplaceZoneType[] = /^\d/u.test(query)
    ? ['ZIP_CODE']
    : ['STATE', 'CITY', 'NEIGHBORHOOD', 'SUBNEIGHBORHOOD'];
  if (zonesOnly) {
    const data = await requestZeusFromBrowser(
      MarketplaceLocationZonesDocument,
      { zoneInput: { query, types } },
      signal,
    );
    return { marketplace: { ...data.marketplace, addressSearch: [] } };
  }
  return requestZeusFromBrowser(
    MarketplaceLocationSuggestionsDocument,
    { propertyInput: { query }, zoneInput: { query, types } },
    signal,
  );
};

const preventMouseDownFocus = (event: ReactMouseEvent) => {
  event.preventDefault();
};

interface RecentSearchRowProps {
  entry: RecentLocationSearchEntry;
  onSelect: (entry: RecentLocationSearchEntry) => void;
  onRemove: (entry: RecentLocationSearchEntry) => void;
}

const MapRefreshRecentSearchRow = ({ entry, onSelect, onRemove }: RecentSearchRowProps) => {
  const handleSelect = () => onSelect(entry);
  const handleRemove = () => onRemove(entry);
  return (
    <Row className="group items-center rounded-xl pr-3 hover:bg-[#FDFCF9]">
      <Box
        aria-label={`Select recent ${recentEntryDisplayName(entry)}`}
        analyticsName="map-refresh-recent-select"
        onClick={handleSelect}
        className="min-w-0 flex-1 justify-start gap-3 px-3 py-3 text-left"
      >
        <IconClock aria-hidden size={20} className="shrink-0 text-od-content-inactive-primary" />
        <Text as="span" className="truncate text-body-sm leading-5">
          {recentEntryDisplayName(entry)}
        </Text>
      </Box>
      <Button
        aria-label={`Remove recent ${recentEntryDisplayName(entry)}`}
        analyticsName="map-refresh-recent-remove"
        onClick={handleRemove}
        variant="secondary"
        size="sm"
        className="size-6 shrink-0 border-0 bg-transparent p-0 text-od-content-inactive-primary shadow-none"
      >
        <IconX aria-hidden size={16} className="text-od-content-inactive-primary" />
      </Button>
    </Row>
  );
};

interface MapRefreshLocationOptionRowProps {
  location: MapLocation;
  name: string;
  subtitle: string;
  parentStateId: string | undefined;
  onSelect: (location: MapLocation) => void;
}

const MapRefreshLocationOptionRow = ({
  location,
  name,
  subtitle,
  parentStateId,
  onSelect,
}: MapRefreshLocationOptionRowProps) => {
  const handleSelect = () => onSelect(location);
  return (
    <Box className={parentStateId ? 'w-full pl-8' : 'w-full'}>
      <Box
        aria-label={`Select ${location.label}`}
        analyticsName="cosmos-map-select-location"
        onClick={handleSelect}
        className={cn(
          'w-full items-center justify-start gap-3 rounded-xl px-3 py-3 text-left hover:bg-[#FDFCF9]',
        )}
      >
        {location.zoneType === 'CITY' ? (
          <IconBuilding
            aria-hidden
            size={20}
            className="shrink-0 text-od-content-inactive-primary"
          />
        ) : (
          <IconMapPin aria-hidden size={20} className="shrink-0 text-od-content-inactive-primary" />
        )}
        <Col className="min-w-0 items-start gap-1">
          <Text as="span" className="text-body-sm leading-5">
            {name}
          </Text>
          <Text as="span" className="text-body-xs leading-4 text-od-content-secondary">
            {subtitle}
          </Text>
        </Col>
      </Box>
    </Box>
  );
};

interface MapRefreshAddressOptionRowProps {
  address: MarketplacePropertySuggestionFragment;
  onSelect: (address: MarketplacePropertySuggestionFragment) => void;
}

const MapRefreshAddressOptionRow = ({ address, onSelect }: MapRefreshAddressOptionRowProps) => {
  const handleSelect = () => onSelect(address);
  return (
    <Box
      aria-label={`Select ${address.fullAddress}`}
      analyticsName="cosmos-map-select-address"
      onClick={handleSelect}
      className="w-full items-center justify-start gap-3 rounded-xl bg-[#F9FCFF] px-3 py-3 text-left hover:bg-od-bg-inactive-accent-brand/20"
    >
      <IconHome aria-hidden size={20} className="shrink-0 text-[#020E60]" />
      <Col className="min-w-0 items-start gap-1">
        <Text as="span" className="text-body-sm leading-5 text-[#020E60]">
          {address.fullAddress}
        </Text>
        {address.listPriceCents != null && address.listPriceCents > 0 ? (
          <Text as="span" className="text-body-xs leading-4 text-[#0266F2]">
            Listed for {formatCurrency(address.listPriceCents)}
          </Text>
        ) : null}
      </Col>
    </Box>
  );
};

const RecentSearchRow = ({ entry, onSelect, onRemove }: RecentSearchRowProps) => {
  const displayName = recentEntryDisplayName(entry);
  const handleSelect = () => onSelect(entry);
  const handleRemove = (event: ReactMouseEvent) => {
    event.stopPropagation();
    onRemove(entry);
  };
  return (
    <Row className="w-full items-center gap-1 border-b border-od-border-secondary pr-2 hover:bg-od-bg-secondary">
      <Box
        aria-label={`Select recent ${displayName}`}
        analyticsName={
          entry.kind === 'zone'
            ? 'cosmos-map-select-recent-location'
            : 'cosmos-map-select-recent-address'
        }
        onClick={handleSelect}
        className="relative min-w-0 flex-1 flex-row items-center justify-start px-4 py-3 text-left active:bg-od-bg-secondary"
      >
        {entry.kind === 'zone' ? (
          <Text as="span" className="w-full truncate whitespace-nowrap text-left text-body-sm">
            {entry.label} {zoneTypeLabel[entry.zoneType]}
          </Text>
        ) : (
          <Col className="min-w-0 flex-1 items-start">
            <Text as="p" className="w-full truncate whitespace-nowrap text-left text-body-sm">
              {displayName}
            </Text>
            {entry.listPriceCents ? (
              <Text as="p" className="text-label-sm self-start">
                {formatCurrency(entry.listPriceCents)}
              </Text>
            ) : null}
          </Col>
        )}
      </Box>
      <Box
        aria-label={`Remove ${displayName} from recent searches`}
        analyticsName="cosmos-map-remove-recent-search"
        onMouseDown={preventMouseDownFocus}
        onClick={handleRemove}
        className="shrink-0 items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary"
      >
        <IconX size={18} aria-hidden />
      </Box>
    </Row>
  );
};

interface LocationOptionRowProps {
  location: MapLocation;
  isFirst: boolean;
  disabled: boolean;
  onSelect: (location: MapLocation) => void;
}

const LocationOptionRow = ({ location, isFirst, disabled, onSelect }: LocationOptionRowProps) => {
  const handleSelect = () => onSelect(location);
  return (
    <Col className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary">
      <Box
        aria-label={`Select ${location.label}`}
        analyticsName="comos-map-select-location"
        onClick={handleSelect}
        disabled={disabled}
        className={cn(
          'relative w-full min-w-0 flex-row items-center justify-start px-4 py-3 text-left active:bg-od-bg-secondary',
          isFirst && 'pt-2',
        )}
      >
        <Text as="span" className="w-full truncate whitespace-nowrap text-left text-body-sm">
          {location.label} {zoneTypeLabel[location.zoneType]}
        </Text>
      </Box>
    </Col>
  );
};

interface AddressOptionRowProps {
  address: MarketplacePropertySuggestionFragment;
  isFirst: boolean;
  disabled: boolean;
  onSelect: (address: MarketplacePropertySuggestionFragment) => void;
}

const AddressOptionRow = ({ address, isFirst, disabled, onSelect }: AddressOptionRowProps) => {
  const handleSelect = () => onSelect(address);
  return (
    <Col className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary">
      <Box
        aria-label={`Select ${address.fullAddress}`}
        analyticsName="cosmos-map-select-address"
        onClick={handleSelect}
        disabled={disabled}
        className={cn(
          'relative w-full min-w-0 flex-row items-center justify-start px-4 py-3 text-left active:bg-od-bg-secondary',
          isFirst && 'pt-2',
        )}
      >
        <Col className="min-w-0 flex-1 items-start">
          <Text as="p" className="w-full truncate whitespace-nowrap text-left text-body-sm">
            {address.fullAddress}
          </Text>
          {address.listPriceCents && (
            <Text as="p" className="text-label-sm self-start">
              {formatCurrency(address.listPriceCents)}
            </Text>
          )}
        </Col>
      </Box>
    </Col>
  );
};

export const MapLocationSearch = ({
  isFocused,
  selection,
  onSelectionChange,
  size = 'sm',
  showLabel = true,
  inputClassName,
  presentation = 'default',
  onNearby,
  onInputEdit,
  label = null,
  onLabelClear,
  nearbyPending = false,
  nearbyError,
  placeholder,
  trailingControl,
  savedSearches,
}: LocationSearchProps) => {
  const [query, setQuery] = useState('');
  const wasNearbyPending = useRef(nearbyPending);
  const inputId = useId();
  const selectionLabel =
    selection?.kind === 'location'
      ? selection.location.label
      : (selection?.address.fullAddress ?? label ?? undefined);
  const hasLocationValue = Boolean(selectionLabel || query);
  const hasTrailingControls = hasLocationValue || Boolean(trailingControl);
  const hasTwoTrailingControls = hasLocationValue && Boolean(trailingControl);
  const normalizedQuery = query.trim();
  const [debouncedQuery] = useDebounce(
    normalizedQuery,
    presentation === 'map-refresh' ? MAP_SEARCH_DEBOUNCE_MS : SEARCH_DEBOUNCE_MS,
  );
  const isEligibleQuery = isSearchableQuery(normalizedQuery);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const resultsRef = useRef<HTMLElement | null>(null);
  // Dialogs opened from the panel portal here, above the page, while staying part of the search.
  const [overlayLayer, setOverlayLayer] = useState<HTMLElement | null>(null);
  const { recentSearches, rememberRecentSearch, removeRecentSearch, clearRecentSearches } =
    useRecentLocationSearches();
  const suggestions = useQuery({
    queryKey:
      presentation === 'map-refresh'
        ? ['marketplace', 'location-suggestions', debouncedQuery, 'zones-only']
        : ['marketplace', 'location-suggestions', debouncedQuery],
    queryFn: ({ signal }) =>
      fetchLocationSuggestions(debouncedQuery, signal, presentation === 'map-refresh'),
    enabled: isSearchableQuery(debouncedQuery),
    placeholderData: presentation === 'map-refresh' ? undefined : keepPreviousData,
    retry: false,
    staleTime: presentation === 'map-refresh' ? MAP_SUGGESTION_CACHE_MS : 30_000,
  });
  // A two-letter state code searches places only, so its address search never runs.
  const addressesEnabled =
    presentation === 'map-refresh' && debouncedQuery.length > QUERY_STRING_MIN_LENGTH;
  const addresses = useQuery({
    queryKey: ['marketplace', 'address-suggestions', debouncedQuery],
    queryFn: ({ signal }) =>
      requestZeusFromBrowser(
        MarketplaceLocationAddressesDocument,
        { propertyInput: { query: debouncedQuery } },
        signal,
      ),
    enabled: addressesEnabled,
    retry: false,
    staleTime: MAP_SUGGESTION_CACHE_MS,
  });
  const isCurrentQuery = debouncedQuery === normalizedQuery;
  const isPending =
    !isCurrentQuery ||
    (presentation === 'map-refresh'
      ? suggestions.isPending || (addressesEnabled && addresses.isPending)
      : suggestions.isFetching);
  const isFetchingSuggestions = isEligibleQuery && isPending;
  const fetchError = isEligibleQuery && isCurrentQuery && suggestions.isError;
  const data = isEligibleQuery ? suggestions.data : undefined;
  const locationOptions = useMemo(() => {
    const zones = (data?.marketplace.searchZones ?? []).map(toRankedMapLocation);
    // The server lists states first, with an exact code ("NE") ahead of
    // name-prefix matches (Nevada); a population sort would undo that.
    const states = zones.filter((zone) => zone.zoneType === 'STATE');
    const rankedLocations = zones.filter((zone) => zone.zoneType !== 'STATE');
    rankedLocations.sort((a, b) =>
      a.population == null || b.population == null ? 0 : b.population - a.population,
    );
    return [...states, ...rankedLocations].map(({ population: _, ...location }) => location);
  }, [data]);
  const refreshLocationOptions = useMemo(() => {
    if (presentation !== 'map-refresh') return [];
    const suggestionsById = new Map(
      (data?.marketplace.searchZones ?? []).map((suggestion) => [suggestion.zone.id, suggestion]),
    );
    const states = new Map(
      (data?.marketplace.searchZones ?? []).flatMap(({ zone }) =>
        zone.type === 'STATE' && zone.stateAbbr ? [[zone.stateAbbr, zone] as const] : [],
      ),
    );
    const rows = locationOptions.map((location) => {
      const suggestion = suggestionsById.get(location.value);
      const stateAbbr = suggestion?.zone.stateAbbr;
      const state = stateAbbr ? states.get(stateAbbr) : undefined;
      let region = suggestion?.displayTextSecondary?.trim() || stateAbbr || '';
      if (state && stateAbbr) {
        if (region === stateAbbr || location.zoneType === 'CITY') region = state.name;
        else if (region.endsWith(`, ${stateAbbr}`))
          region = `${region.slice(0, -stateAbbr.length)}${state.name}`;
      }
      const type =
        location.zoneType === 'ZIP_CODE'
          ? 'Postal code'
          : zoneTypeLabel[location.zoneType].replace(/[()]/g, '') || 'Area';
      return {
        location,
        name: suggestion?.displayTextPrimary?.trim() || suggestion?.zone.name || location.label,
        subtitle:
          location.zoneType === 'STATE'
            ? 'State in the USA'
            : `${type}${region ? ` in ${region}` : ''}`,
        parentStateId: location.zoneType === 'CITY' ? state?.id : undefined,
      };
    });
    return rows.flatMap((row) =>
      row.parentStateId
        ? []
        : [row, ...rows.filter((child) => child.parentStateId === row.location.value)],
    );
  }, [data, locationOptions, presentation]);
  const currentAddresses =
    isEligibleQuery && isCurrentQuery ? addresses.data?.marketplace.addressSearch : undefined;
  const combinedAddresses = data?.marketplace.addressSearch;
  const addressOptions =
    (presentation === 'map-refresh' ? currentAddresses : combinedAddresses) ?? [];
  const addressError =
    presentation === 'map-refresh' && isEligibleQuery && isCurrentQuery && addresses.isError;
  useEffect(() => {
    if (addresses.error) reportLocationSuggestionsError(addresses.error);
  }, [addresses.error]);

  useEffect(() => {
    if (suggestions.error) {
      reportLocationSuggestionsError(suggestions.error);
    }
  }, [suggestions.error]);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollTop = 0;
    }
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [suggestions.data, suggestions.error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        !containerRef.current?.contains(event.target) &&
        !overlayLayer?.contains(event.target)
      ) {
        setIsLocationFocused(false);
        setIsDropdownFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [overlayLayer]);

  useEffect(() => {
    if (!overlayLayer) return undefined;
    // A dialog whose opener is gone or disabled after it closes (a deleted row, a
    // search that is now saved) drops focus to the page; return it to the search.
    const observer = new MutationObserver(() => {
      if (overlayLayer.childElementCount > 0) return;
      window.requestAnimationFrame(() => {
        if (document.activeElement === document.body) {
          inputRef.current?.focus({ preventScroll: true });
        }
      });
    });
    observer.observe(overlayLayer, { childList: true });
    return () => observer.disconnect();
  }, [overlayLayer]);

  const handleLocationInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      wasNearbyPending.current = false;
      onInputEdit?.();
      if (selection) {
        onSelectionChange(null);
      } else if (label) {
        onLabelClear?.();
      }
      setQuery(event.target.value);
    },
    [onInputEdit, onSelectionChange, selection, label, onLabelClear],
  );

  const selectLocationLabel = useCallback(
    (input: HTMLInputElement) => {
      if (selection || label) {
        input.select();
      }
    },
    [selection, label],
  );

  const handleLocationFocus = useCallback(
    (event: ReactFocusEvent<HTMLInputElement>) => {
      setIsLocationFocused(true);
      selectLocationLabel(event.currentTarget);
    },
    [selectLocationLabel],
  );

  const handleLocationClick = useCallback(
    (event: ReactMouseEvent<HTMLInputElement>) => {
      selectLocationLabel(event.currentTarget);
    },
    [selectLocationLabel],
  );

  const handleLocationClear = useCallback(() => {
    wasNearbyPending.current = false;
    onInputEdit?.();
    setQuery('');
    if (selection) {
      onSelectionChange(null);
    } else if (label) {
      onLabelClear?.();
    }
    setIsLocationFocused(true);
    inputRef.current?.focus();
  }, [onInputEdit, onSelectionChange, selection, label, onLabelClear]);
  const handleDropdownFocus = () => setIsDropdownFocused(true);
  const handleDropdownBlur = useCallback(() => {
    setIsLocationFocused(false);
    setIsDropdownFocused(false);
  }, []);
  const isWithinSearch = useCallback(
    (node: Node) => Boolean(containerRef.current?.contains(node) || overlayLayer?.contains(node)),
    [overlayLayer],
  );
  const handleSearchBlur = useCallback(
    (event: ReactFocusEvent<HTMLElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node) {
        if (!isWithinSearch(next)) handleDropdownBlur();
        return;
      }
      const blurred = event.target;
      // Focus also drops to the page when the row or dialog that held it unmounts;
      // only a real move away from the search closes the panel.
      window.requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && active !== document.body && isWithinSearch(active)) return;
        if (!blurred.isConnected || overlayLayer?.contains(blurred)) return;
        handleDropdownBlur();
      });
    },
    [handleDropdownBlur, isWithinSearch, overlayLayer],
  );
  const handleResultsKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Escape' || !targetsOwnSubtree(event)) return;
      inputRef.current?.focus({ preventScroll: true });
      handleDropdownBlur();
    },
    [handleDropdownBlur],
  );
  const handleSavedSearchApply = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => {
      handleDropdownBlur();
      savedSearches?.onApplySavedSearch(savedSearch);
    },
    [handleDropdownBlur, savedSearches],
  );
  // A removed saved search leaves with focus on its card; keep it in the search.
  const handleSavedSearchRemovedWithFocus = useCallback(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);
  const handleSavedSearchOpenFilters = useCallback(() => {
    handleDropdownBlur();
    savedSearches?.onOpenFilters?.();
  }, [handleDropdownBlur, savedSearches]);
  useEffect(() => {
    if (wasNearbyPending.current && !nearbyPending && !nearbyError) {
      setIsLocationFocused(false);
      setIsDropdownFocused(false);
    }
    wasNearbyPending.current = nearbyPending;
  }, [nearbyPending, nearbyError]);

  const handleLocationSelect = (location: MapLocation) => {
    rememberRecentSearch(recentEntryFromLocation(location));
    onSelectionChange({ kind: 'location', location });
    handleDropdownBlur();
    setQuery('');
  };

  const handleAddressSelect = (address: MarketplacePropertySuggestionFragment) => {
    rememberRecentSearch(recentEntryFromAddress(address));
    onSelectionChange({ kind: 'address', address });
    handleDropdownBlur();
    setQuery('');
  };

  const handleRecentSelect = (entry: RecentLocationSearchEntry) => {
    if (entry.kind === 'zone') {
      handleLocationSelect(mapLocationFromRecentEntry(entry));
      return;
    }
    handleAddressSelect(addressFromRecentEntry(entry));
  };

  const handleRecentRemove = (entry: RecentLocationSearchEntry) => {
    removeRecentSearch(entry);
  };

  const hasSuggestionResults = locationOptions.length > 0 || addressOptions.length > 0;

  const canShowResults = isEligibleQuery && !isFetchingSuggestions && !fetchError && !addressError;
  const showNoResultsMessage = canShowResults && !hasSuggestionResults;

  const showRecentSection =
    query.length === 0 && recentSearches.length > 0 && (isLocationFocused || isDropdownFocused);

  const shouldShowDropdown =
    showRecentSection ||
    (Boolean(query) &&
      (isLocationFocused || isDropdownFocused) &&
      (hasSuggestionResults || showNoResultsMessage || isFetchingSuggestions || fetchError));

  // The floor keeps the transient single-line states (searching, error, empty)
  // from rendering as a sliver; rows already exceed it and would otherwise sit
  // above dead space.
  const showsRows = showRecentSection || (Boolean(query) && hasSuggestionResults);

  if (presentation === 'map-refresh') {
    const hasQueryResults =
      isFetchingSuggestions ||
      fetchError ||
      addressError ||
      showNoResultsMessage ||
      (isCurrentQuery && hasSuggestionResults);
    const hasIdleResults = Boolean(onNearby) || recentSearches.length > 0 || Boolean(savedSearches);
    const showResults =
      (isLocationFocused || isDropdownFocused) &&
      (Boolean(nearbyError) || (query ? hasQueryResults : hasIdleResults));
    const firstLocation = query && isCurrentQuery ? refreshLocationOptions[0]?.location : undefined;
    const firstAddress = query && isCurrentQuery ? addressOptions[0] : undefined;
    const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        handleDropdownBlur();
        return;
      }
      if (event.key !== 'Enter') return;
      if (firstLocation) handleLocationSelect(firstLocation);
      else if (firstAddress) handleAddressSelect(firstAddress);
    };
    return (
      <Row
        ref={containerRef}
        className="h-12 min-w-0 flex-1 items-center gap-2 px-3"
        onBlur={handleSearchBlur}
      >
        <IconMapPin aria-hidden size={20} strokeWidth={1.5} className="shrink-0 text-[#A4A19B]" />
        <Input
          ref={inputRef}
          aria-label="Location search"
          id={inputId}
          name="locations"
          size="xs"
          placeholder={placeholder}
          value={selectionLabel ?? query}
          onChange={handleLocationInputChange}
          onFocus={handleLocationFocus}
          onClick={handleLocationClick}
          onKeyDown={handleKeyDown}
          className="h-full min-w-0 w-full rounded-none border-0 bg-transparent px-0 text-body-md shadow-none ring-0 placeholder:text-[#89867F] focus:ring-0 focus-visible:ring-0"
        />
        {hasLocationValue ? (
          <Button
            aria-label="Clear location search"
            analyticsName="map-refresh-location-clear"
            onClick={handleLocationClear}
            variant="secondary"
            size="sm"
            className="size-6 shrink-0 border-0 bg-transparent p-0 text-od-content-inactive-primary shadow-none"
          >
            <IconX aria-hidden size={20} className="text-od-content-inactive-primary" />
          </Button>
        ) : null}
        {trailingControl}
        {showResults ? <style>{RESULTS_SCROLLBAR}</style> : null}
        {showResults ? (
          <Col
            ref={resultsRef}
            data-location-results
            aria-label="Location suggestions"
            className="absolute top-[calc(100%+8px)] left-0 z-20 max-h-[min(520px,65vh)] w-full gap-1 overflow-y-auto rounded-3xl border border-od-border-secondary bg-od-bg-primary p-3 shadow-[0_8px_24px_rgba(35,30,27,0.08)]"
            onFocus={handleDropdownFocus}
            onMouseDown={keepInputFocusOnRowPress}
            onKeyDown={handleResultsKeyDown}
          >
            {!query && onNearby ? (
              <Box
                aria-label="Find homes nearby"
                analyticsName="map-refresh-nearby"
                onClick={onNearby}
                disabled={nearbyPending}
                className="w-full items-center justify-start gap-3 rounded-xl px-3 py-3 text-left hover:bg-[#FDFCF9]"
              >
                <IconLocation
                  aria-hidden
                  size={20}
                  className="shrink-0 text-od-content-inactive-primary"
                />
                <Col className="items-start gap-1">
                  <Text as="span" className="text-body-sm leading-5">
                    {nearbyPending ? 'Finding your location…' : 'Nearby'}
                  </Text>
                  <Text as="span" className="text-body-xs leading-4 text-od-content-secondary">
                    Houses closest to you
                  </Text>
                </Col>
              </Box>
            ) : null}
            {nearbyError ? (
              <Text role="alert" className="px-3 py-2 text-body-xs text-od-content-error">
                {nearbyError}
              </Text>
            ) : null}
            {!query && recentSearches.length > 0 ? (
              <>
                <Row className="items-center justify-between pb-2 pl-3 pr-1 pt-3">
                  <Text as="p" className="text-label-sm">
                    Recent searches
                  </Text>
                  <Button
                    analyticsName="map-refresh-recent-clear"
                    aria-label="Clear all recent searches"
                    onClick={clearRecentSearches}
                    variant="secondary"
                    size="sm"
                    className="h-7 rounded-full border-0 bg-transparent px-2 text-body-xs text-od-content-secondary shadow-none not-disabled:hover:bg-[#FDFCF9] not-disabled:hover:text-od-content-primary"
                  >
                    Clear all
                  </Button>
                </Row>
                {recentSearches.map((entry) => (
                  <MapRefreshRecentSearchRow
                    key={recentEntryIdentity(entry)}
                    entry={entry}
                    onSelect={handleRecentSelect}
                    onRemove={handleRecentRemove}
                  />
                ))}
              </>
            ) : null}
            {!query && savedSearches ? (
              <AtomsPortalProvider container={overlayLayer}>
                <SavedSearchesSection
                  {...savedSearches}
                  onApplySavedSearch={handleSavedSearchApply}
                  onRemovedWithFocus={handleSavedSearchRemovedWithFocus}
                  onOpenFilters={
                    savedSearches.onOpenFilters ? handleSavedSearchOpenFilters : undefined
                  }
                />
              </AtomsPortalProvider>
            ) : null}
            {isFetchingSuggestions ? (
              <Text role="status" className="px-3 py-4 text-body-sm text-od-content-secondary">
                Searching…
              </Text>
            ) : null}
            {fetchError ? (
              <Text role="alert" className="px-3 py-4 text-body-sm">
                Couldn’t load locations. Please try again.
              </Text>
            ) : null}
            {showNoResultsMessage ? (
              <Text className="px-3 py-4 text-body-sm text-od-content-secondary">
                No matching locations found.
              </Text>
            ) : null}
            {addressError ? (
              <Text role="alert" className="px-3 py-4 text-body-sm">
                Couldn’t load addresses. You can still search locations.
              </Text>
            ) : null}
            {query && isCurrentQuery ? (
              <>
                {refreshLocationOptions.map(({ location, name, subtitle, parentStateId }) => (
                  <MapRefreshLocationOptionRow
                    key={location.value}
                    location={location}
                    name={name}
                    subtitle={subtitle}
                    parentStateId={parentStateId}
                    onSelect={handleLocationSelect}
                  />
                ))}
                {addressOptions.map((address) => (
                  <MapRefreshAddressOptionRow
                    key={address.addressId}
                    address={address}
                    onSelect={handleAddressSelect}
                  />
                ))}
              </>
            ) : null}
          </Col>
        ) : null}
        {showResults && savedSearches
          ? createPortal(
              <Box
                ref={setOverlayLayer}
                className="pointer-events-none fixed inset-0 z-[3202] [&>*]:pointer-events-auto"
              />,
              document.body,
            )
          : null}
      </Row>
    );
  }

  return (
    <Col className="w-full" ref={containerRef}>
      {showLabel ? (
        <Text
          as="label"
          htmlFor={inputId}
          className="text-label-md pb-3 text-od-content-primary max-md:hidden"
        >
          Location
        </Text>
      ) : null}
      <Box className="relative w-full">
        <Input
          ref={inputRef}
          aria-label="Location search"
          autoComplete="off"
          id={inputId}
          name="locations"
          size={size}
          placeholder="Search"
          onChange={handleLocationInputChange}
          onClick={handleLocationClick}
          onFocus={handleLocationFocus}
          value={selectionLabel ?? query}
          className={cn(
            'w-full',
            inputClassName,
            hasTwoTrailingControls ? 'pr-20' : hasTrailingControls && 'pr-11',
            isFocused && 'max-md:pl-10',
          )}
        />
        {hasTrailingControls ? (
          <Row className="absolute right-1.5 top-1/2 items-center gap-1 -translate-y-1/2">
            {hasLocationValue ? (
              <Button
                analyticsName="cosmos-map-clear-location-search"
                aria-label="Clear location search"
                onClick={handleLocationClear}
                variant="secondary"
                size="sm"
                className="size-8 rounded-full border-transparent bg-transparent p-0 text-od-content-secondary hover:bg-od-bg-hover-tertiary"
              >
                <IconX size={18} aria-hidden />
              </Button>
            ) : null}
            {trailingControl}
          </Row>
        ) : null}
      </Box>
      <Box className="relative w-full">
        <Col
          ref={resultsRef}
          aria-label="Location suggestions"
          className={cn(
            'absolute left-0 top-2 z-[10000] max-h-100 w-full overflow-x-hidden overflow-y-auto rounded-2xl border border-od-border-primary bg-od-bg-primary shadow-lg',
            showsRows ? 'min-h-0' : 'min-h-30',
            shouldShowDropdown ? 'flex' : 'hidden',
          )}
          onFocus={handleDropdownFocus}
          onBlur={handleDropdownBlur}
        >
          {showRecentSection ? (
            <Col className="w-full bg-od-bg-primary">
              <Row className="w-full items-center justify-between pl-5 pr-3 pt-2">
                <Text as="p" className="text-label-sm">
                  Recent
                </Text>
                <Box
                  aria-label="Clear all recent searches"
                  analyticsName="cosmos-map-clear-recent-searches"
                  onMouseDown={preventMouseDownFocus}
                  onClick={clearRecentSearches}
                  className="rounded-lg px-2 py-1 hover:bg-od-bg-secondary"
                >
                  <Text as="span" className="text-label-sm text-od-content-secondary">
                    Clear all
                  </Text>
                </Box>
              </Row>
              {recentSearches.map((entry) => (
                <RecentSearchRow
                  key={recentEntryIdentity(entry)}
                  entry={entry}
                  onSelect={handleRecentSelect}
                  onRemove={handleRecentRemove}
                />
              ))}
            </Col>
          ) : null}
          {isFetchingSuggestions ? (
            <Box className="w-full px-5 py-5">
              <Text as="p" className="text-body-sm text-od-content-secondary">
                Searching…
              </Text>
            </Box>
          ) : null}
          {fetchError ? (
            <Box className="w-full px-5 py-5" role="alert">
              <Text as="p" className="text-body-sm text-od-content-primary">
                Something went wrong. Please try again.
              </Text>
            </Box>
          ) : null}
          {showNoResultsMessage ? (
            <Col className="w-full gap-2 px-5 py-5" role="status" aria-live="polite">
              <Text as="p" className="text-body-sm text-od-content-primary">
                No matches yet
              </Text>
              <Text as="p" className="text-label-sm text-od-content-secondary">
                Try a street number and name, ZIP code, city, or state. You can also pan the map to
                search this area.
              </Text>
            </Col>
          ) : null}
          {query && locationOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="px-4 pb-1 pt-3 text-label-sm">
                Locations
              </Text>
              {locationOptions.map((location, index) => (
                <LocationOptionRow
                  key={location.value}
                  location={location}
                  isFirst={index === 0}
                  disabled={isFetchingSuggestions || fetchError}
                  onSelect={handleLocationSelect}
                />
              ))}
            </Col>
          ) : null}
          {query && addressOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="px-4 pb-1 pt-4 text-label-sm">
                Listings
              </Text>
              {addressOptions.map((address, index) => (
                <AddressOptionRow
                  key={address.addressId}
                  address={address}
                  isFirst={index === 0}
                  disabled={isFetchingSuggestions || fetchError}
                  onSelect={handleAddressSelect}
                />
              ))}
            </Col>
          ) : null}
        </Col>
      </Box>
    </Col>
  );
};
